import { Component, OnInit } from '@angular/core';
import { DataGridTemplate } from '../../../../../shared/data-grid/data-grid.template';
import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import { UserService, RoleService, UserProfileService } from '../../../../mesc/mesc.service';
import { FormDialogTemplate } from '../../../../../shared/form-dialog/form-dialog.template';
import { BehaviorSubject, Subject } from 'rxjs';
import { AuthenticationService } from '../../../../../auth.service';
import { CgpAlertDialogService } from '../../../../../shared/cgp-alert-dialog/cgp-alert-dialog.service';
import { FilterableService } from '../../../../mesc/filterable-service';
import { CtwrLocationService } from '../../../ctwr.service';

import { utilities } from '../../../../../../modules/utility';
import { takeUntil } from 'rxjs/operators';

const GM_USER_ID_PATTERN = '^([a-zA-Z0-9]){6}$|^([0-9]){9}$';

@Component({
    selector: 'app-users',
    templateUrl: './users.component.html',
    styleUrls: ['./users.component.scss']
})
export class UsersComponent extends DataGridTemplate implements OnInit {
    editForm: FormGroup;
    formDialog: FormDialogTemplate;
    dialogTitle: BehaviorSubject<string> = new BehaviorSubject<string>(undefined);
    module: any;
    users: FilterableService = new FilterableService();
    private readonly ngUnsubscribe: Subject<void> = new Subject<void>();


    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly userService: UserService,
        private readonly roleService: RoleService,
        private readonly locationService: CtwrLocationService,
        private readonly userProfileService: UserProfileService,
        private readonly authService: AuthenticationService,
        private readonly alertDialog: CgpAlertDialogService
    ) {
        super();
    }

    ngOnInit() {
        this.formDialog = new FormDialogTemplate();
        this.modal = true;
        this.filterable = 'menu';
        this.sortable = true;
        this.formDialog.setHeight(480);
        this.gridState.sort = [{ dir: 'asc', field: 'FirstName' }];
        this.controls.customButtons = this.customButtons;
        this.editForm = this.formBuilder.group({
            Id: [],
            FirstName: ['', Validators.required],
            MiddleName: [],
            LastName: [],
            Role: [],
            UserType: [],
            EmployeeStatus: [],
            Region: [],
            Email: [],
            Phone: [],
            LastLoginDate: [],
            Gmin: [0, Validators.compose([Validators.required, Validators.pattern(GM_USER_ID_PATTERN)])],
            RoleIds: [0, Validators.required],
            LocationCity: [''],
        })

        this.formDialog.initilize(this.dialogTitle, this.editForm, this.formDialogTabs);

        this.module = this.authService.getActiveModule().value;
        this.authService.getActiveModule()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(x => {
                this.module = x;
                if (x) {
                    this.roleService.read(`/GetByModule/${this.module.Id}`);
                    this.refresh();
                }
            });

        this.dialogTitle.next('User');
        this.locationService.read();
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
        this.dialogTitle.complete();
    }


    read(opts: any): Promise<any> {
        return opts.force && this.module ?
            this.userService.read(`/role/${this.module.Id}`)
                .then(x => {
                    x.map(u => {
                        if (u.Roles instanceof Array) u.Roles = u.Roles.join(', ');
                        if (u.LastLoginDate) {
                            u.LastLoginDate = new Date(u.LastLoginDate)
                        }
                        return u;
                    });
                    this.users.next(x);
                    return x;
                }) :
            Promise.resolve(this.users.value);
    }


    create(parentEditDataItem: any): void {
        this.formDialog.isNew = true;
        this.editForm.get('Gmin').enable();
        this.editForm.removeControl('LastLoginDate');
        this.formDialog.next({});
    }


    edit(dataItem: any): void {
        this.formDialog.isNew = false;
        const path = `/${this.module.Id}/${dataItem.Id}`;
        this.editForm.addControl('LastLoginDate', new FormControl());
        this.userService.read(path)
            .then(x => {
                this.editForm.get('Gmin').disable();
                this.formDialog.next(x);
            })
    }


    save(dataItem: any, isNew: boolean): Promise<any> {
        const _path = isNew ? `/AddUserWithRoles/${this.module.Id}` : `/${this.module.Id}`;
        return this.userService.save(dataItem, isNew, _path, false)
            .then(x => {
                if (dataItem.Gmin === this.authService.getUserGMIN()) {
                    this.userProfileService.read('');
                }
            });
    }

    getDeleteMessage = (dataItem: any): string => {
        return `Are you sure you want to remove ${dataItem.FirstName} ${dataItem.LastName}?\r\n\r\n*Delegate any active requests belonging to this user.`;
    }


    delete(dataItem: any): Promise<any> {
        return this.userService.remove(`/${this.module.Id}/${dataItem.Id}`, false);
    }


    onSearch = (e, control: any) => {
        const ctrl = this.editForm.get(control.name);
        if (ctrl.value) {
            this.userService.getCGPUser(ctrl.value)
                .then(obj => {
                    if (!obj.FirstName) {
                        this.alertDialog.showAlertDialog({
                            title: "Error",
                            message: "Invalid GMIN/GMID",
                            alert: 'danger',
                            closeLabel: 'Close',
                        });
                        return;
                    }
                    const roles = this.editForm.value.Roles;
                    const c = this.editForm.get(control.name);
                    this.editForm.reset(obj);
                    this.editForm.get('RoleIds').reset(roles);
                    this.editDataItem = obj;
                    c.disable();
                    c.markAsPristine();
                    c.markAsUntouched();
                })
                .catch(e => console.error(e));
        }
    }


    onClear = (e, control) => {
        const roles = this.editForm.value.Roles;
        this.editForm.reset({ Roles: roles });
        this.editForm.get('Gmin').enable();
    }

    handleRefresh = (button, dataItem) => {
        this.userService.save({}, false, `/RefrteshUser/${dataItem.Id}`)
            .then(x => this.refresh());
    }

    customButtons = [{
        title: 'Refresh user profile data.',
        iconClass: 'fa fa-refresh',
        callback: this.handleRefresh
    }]


    columns: any[] = [
        /*{
            field: "Gmin",
            title: "",
            width: 55,
            isFilterDisabled: true,
            template: {
                type: 'userPhoto'
            }
        },*/
        {
            field: 'FirstName',
            title: 'First Name',
        },
        {
            field: 'LastName',
            title: 'Last Name',
        },
        {
            field: 'Email',
            title: 'Email',
            template: {
                type: 'email'
            }
        },
        {
            field: 'Roles',
            title: 'Roles',
        },
        {
            field: 'LocationCity',
            title: 'Location',
        },
        {
            field: 'LastLoginDate',
            title: 'Last Login Date',
            template: {
                type: 'date'
            }
        }
    ];


    formControls = [
        {
            type: "person",
            name: "Gmin",
            title: "GMIN/GMID",
            stretch: true,
            search: this.onSearch,
            clear: this.onClear
        },
        {
            type: "multiselect",
            title: "Roles",
            name: "RoleIds",
            textField: "Name",
            valueField: "Id",
            service: this.roleService,
            stretch: true
        },
        {
            type: "value",
            name: "LocationCity",
            title: "Location City",
            stretch: true
        },
        {
            type: "date-value",
            title: "Last Login Date",
            name: "LastLoginDate",
        }
    ]

    formDialogTabs = [
        {
            title: "User",
            selected: true,
            formControls: this.formControls
        },
    ]

}
